/**
 * SMSRecruiterCard — TRITAL Care® global text-a-recruiter card.
 *
 * One reusable component, three layouts:
 *   variant="default" → full premium card, two clearly separated numbers
 *   variant="compact" → horizontal slim card, ideal for column inserts and dashboards
 *   variant="inline"  → ghost-style line: "Prefer texting? Text our recruiter →"
 *
 * Tap-to-text on mobile uses sms:+1xxxxxxxxxx URIs. Desktop adds a copy-to-clipboard
 * affordance with sonner toast feedback. Numbers per global brand spec:
 *   Recruiter (Jobs): (832) 344-1525  → sms:+18323441525
 *   Compliance (Docs): (218) 250-2526 → sms:+12182502526
 *
 * Tone: warm, non-pushy, reassuring. Copy is curated below; consumers can override
 * `headline` or `subtext` per placement (CLINICIANS_HERO_COPY etc. exported below).
 */

import React, { useState } from "react";
import { motion } from "framer-motion";
import { MessageSquare, FileText, Briefcase, Copy, Check, Sparkles } from "lucide-react";
import { toast } from "sonner";
import {
  Tooltip, TooltipTrigger, TooltipContent, TooltipProvider,
} from "./ui/tooltip";
import { trackEvent } from "../lib/analytics";

/* ---------------- Numbers (single source of truth) ---------------- */
export const RECRUITER_PHONE_DISPLAY  = "(832) 344-1525";
export const RECRUITER_SMS_HREF       = "sms:+18323441525";
export const COMPLIANCE_PHONE_DISPLAY = "(218) 250-2526";
export const COMPLIANCE_SMS_HREF      = "sms:+12182502526";

/* ---------------- Curated copy variants per placement ---------------- */
export const SMS_COPY = {
  default:    { headline: "Get matched with the right opportunity faster.",          sub: "Text our recruiter directly — no calls, no pressure." },
  hero:       { headline: "Prefer to text? We do that here.",                        sub: "Skip the call. A recruiter responds to texts inside one business hour." },
  jobs:       { headline: "Saw a job that fits? Text first — apply when ready.",     sub: "Faster than the form. Same recruiter who'd call you back." },
  prn:        { headline: "PRN coverage gap or last-minute shift?",                  sub: "Text the recruiter desk. Open weekends and after-hours." },
  services:   { headline: "Not sure which service fits your need?",                  sub: "Text us a sentence about your situation — we'll route you to the right team." },
  compliance: { headline: "Document, license or certification question?",            sub: "Our compliance desk replies to texts inside one business hour." },
  checklist:  { headline: "Need help validating a checklist?",                       sub: "Text our compliance desk — share a screenshot, we'll review and reply." },
  contact:    { headline: "Already filled the form? Or skip it.",                    sub: "Both numbers are real humans on TRITAL Care's recruiter desk." },
  careers:    { headline: "Quick question about a role?",                            sub: "Text the recruiter — we keep it human, not robotic." },
};

/* ---------------- Inline micro-CTA variants ---------------- */
export const INLINE_CTAS = {
  form_general:    "Prefer texting? Message a recruiter instead",
  form_employer:   "Skip the form — text us directly",
  form_clinician:  "Quick question? Text us",
};

/* ---------------- Component ---------------- */
export default function SMSRecruiterCard({
  variant = "default",
  headline,
  subtext,
  className = "",
  testId = "sms-recruiter-card",
}) {
  const copy = (variant === "inline" || variant === "compact") ? null : SMS_COPY.default;
  const finalHeadline = headline ?? copy?.headline;
  const finalSub      = subtext  ?? copy?.sub;

  if (variant === "inline") return <InlineCTA href={RECRUITER_SMS_HREF} label={headline || INLINE_CTAS.form_general} testId={testId} />;
  if (variant === "compact") return <CompactCard headline={finalHeadline ?? "Text a recruiter"} sub={finalSub ?? "No calls, no pressure"} className={className} testId={testId} />;

  return (
    <motion.section
      initial={{ opacity: 0, y: 8 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.35 }}
      className={`relative rounded-3xl border border-trital-medical-blue-soft/80 shadow-card overflow-hidden ${className}`}
      data-testid={testId}
      aria-labelledby={`${testId}-heading`}
    >
      <div className="absolute inset-0 bg-gradient-to-br from-trital-medical-blue-soft/70 via-white to-white pointer-events-none" />
      <div className="absolute -top-20 -right-20 w-56 h-56 rounded-full bg-trital-medical-blue-soft blur-3xl opacity-80 pointer-events-none" />
      <div className="relative p-6 lg:p-7 grid lg:grid-cols-[1fr_auto] gap-6 items-center">
        {/* Headline column */}
        <div className="min-w-0">
          <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white border border-slate-200 shadow-sm">
            <Sparkles className="w-3 h-3 text-trital-red" strokeWidth={2} />
            <span className="overline text-trital-navy">Text us · faster than calling</span>
          </div>
          <h3
            id={`${testId}-heading`}
            className="mt-3 font-display font-black text-[22px] lg:text-[26px] leading-tight text-slate-900 text-balance"
          >
            {finalHeadline}
          </h3>
          {finalSub && (
            <p className="mt-2 text-[13.5px] lg:text-[14px] text-slate-600 max-w-[58ch] leading-relaxed">{finalSub}</p>
          )}
          <div className="mt-2 text-[11.5px] text-slate-500 inline-flex items-center gap-1.5">
            <MessageSquare className="w-3 h-3 text-trital-navy" strokeWidth={1.8} />
            Average reply: under 1 business hour · Mon–Sat
          </div>
        </div>

        {/* Numbers column */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-1 gap-3 min-w-[280px]">
          <NumberTile
            kind="recruiter"
            href={RECRUITER_SMS_HREF}
            onClick={() => trackEvent("sms_recruiter_click", { surface: "sms-card-default", variant })}
            phone={RECRUITER_PHONE_DISPLAY}
            label="Recruiter (Jobs)"
            Icon={Briefcase}
            tone="navy"
            testId={`${testId}-recruiter`}
          />
          <NumberTile
            kind="compliance"
            href={COMPLIANCE_SMS_HREF}
            phone={COMPLIANCE_PHONE_DISPLAY}
            label="Compliance (Docs)"
            Icon={FileText}
            tone="red"
            testId={`${testId}-compliance`}
          />
        </div>
      </div>
    </motion.section>
  );
}

/* ---------------- Sub-pieces ---------------- */

function NumberTile({ href, phone, label, Icon, tone, testId, kind }) {
  const [copied, setCopied] = useState(false);
  const palette = tone === "red"
    ? { ic: "bg-red-50 text-trital-red",      ring: "hover:border-trital-red/60" }
    : { ic: "bg-trital-medical-blue-soft text-trital-navy", ring: "hover:border-trital-navy/50" };

  const onCopy = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(phone);
      setCopied(true);
      toast.success(`${label} number copied`, { description: phone });
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("Couldn't copy — long-press the number to copy manually.");
    }
  };

  return (
    <TooltipProvider delayDuration={150}>
      <div className="relative">
        <Tooltip>
          <TooltipTrigger asChild>
            <a
              href={href}
              data-testid={testId}
              data-sms-kind={kind}
              className={`group relative flex items-center gap-3 rounded-2xl bg-white border border-slate-200 p-3.5 ${palette.ring} transition-colors shadow-sm hover:shadow-card-hover focus:outline-none focus:ring-2 focus:ring-trital-navy/30`}
              aria-label={`Text ${label} at ${phone}`}
            >
              <div className={`w-10 h-10 rounded-xl ${palette.ic} flex items-center justify-center shrink-0`}>
                <Icon className="w-5 h-5" strokeWidth={1.8} />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-[10.5px] font-bold uppercase tracking-[0.14em] text-slate-500">{label}</div>
                <div className="font-display font-bold text-[16px] text-slate-900 group-hover:text-trital-navy tabular-nums">{phone}</div>
              </div>
              <button
                type="button"
                onClick={onCopy}
                className="hidden md:inline-flex items-center justify-center w-8 h-8 rounded-lg text-slate-400 hover:text-trital-navy hover:bg-slate-50 shrink-0"
                aria-label={`Copy ${label} number`}
                data-testid={`${testId}-copy`}
              >
                {copied ? <Check className="w-4 h-4 text-emerald-600" strokeWidth={2} /> : <Copy className="w-4 h-4" strokeWidth={1.8} />}
              </button>
            </a>
          </TooltipTrigger>
          <TooltipContent side="top" className="text-[11.5px]">
            Text this number
          </TooltipContent>
        </Tooltip>
      </div>
    </TooltipProvider>
  );
}

function CompactCard({ headline, sub, className, testId }) {
  return (
    <div
      className={`rounded-2xl border border-slate-200 bg-gradient-to-br from-trital-medical-blue-soft/60 via-white to-white p-4 shadow-card ${className}`}
      data-testid={testId}
    >
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl bg-trital-navy text-white flex items-center justify-center shrink-0">
          <MessageSquare className="w-5 h-5" strokeWidth={1.8} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-display font-bold text-[14px] text-slate-900 leading-snug">{headline}</div>
          <div className="mt-0.5 text-[11.5px] text-slate-600">{sub}</div>
          <div className="mt-2 flex flex-wrap gap-1.5">
            <a
              href={RECRUITER_SMS_HREF}
            onClick={() => trackEvent("sms_recruiter_click", { surface: "sms-card-default", variant })}
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white border border-slate-200 text-[11.5px] font-semibold text-trital-navy hover:bg-trital-medical-blue-soft"
              data-testid={`${testId}-recruiter`}
              aria-label={`Text Recruiter at ${RECRUITER_PHONE_DISPLAY}`}
            >
              <Briefcase className="w-3 h-3" strokeWidth={1.8} /> Recruiter · {RECRUITER_PHONE_DISPLAY}
            </a>
            <a
              href={COMPLIANCE_SMS_HREF}
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white border border-slate-200 text-[11.5px] font-semibold text-trital-red hover:bg-red-50"
              data-testid={`${testId}-compliance`}
              aria-label={`Text Compliance at ${COMPLIANCE_PHONE_DISPLAY}`}
            >
              <FileText className="w-3 h-3" strokeWidth={1.8} /> Compliance · {COMPLIANCE_PHONE_DISPLAY}
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

function InlineCTA({ href, label, testId }) {
  return (
    <a
      href={href}
      className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-trital-navy hover:text-trital-red transition-colors group"
      data-testid={testId}
      aria-label={`${label} (text recruiter)`}
    >
      <MessageSquare className="w-3.5 h-3.5 text-trital-red" strokeWidth={1.8} />
      {label}
      <span aria-hidden className="transition-transform group-hover:translate-x-0.5">→</span>
    </a>
  );
}
